import { useEffect, useMemo, useRef } from 'react'

import { useContainerSize } from '@/hooks/useContainerSize'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLastData } from '@/hooks/useLastData'
import { useRouter } from 'next/router'
import { HiOutlineOfficeBuilding } from 'react-icons/hi'

import { Box, Flex, Skeleton } from '@chakra-ui/react'

import { ActionButtons } from '@/components/layouts/ActionButtons'
import { EditModeFrame } from '@/components/layouts/EditMode'
import { Head } from '@/components/layouts/Head'
import { DarkAppHeader, Header } from '@/components/layouts/Header'
import { PageLayout } from '@/components/layouts/PageLayout'
import { PageLoader, PageSpinnerToast } from '@/components/ui'
import { PreloadCannedVideos } from '@/features/canned-video'
import { GlobalMap, trackOrganisationInfo } from '@/features/global-map'
import {
  useGlobalFacilitiesQuery,
  useIncidentsByMarketFacilityQuery,
  useOrganizationFacilitiesIncidentCountQuery,
  useOrganizationQuery,
} from '@/graphql/generated/hooks'
import { GlobalFacility } from '@/graphql/generated/schemas'
import { startTimer } from '@/utils/analytics'
import { requireAuth } from '@/utils/auth/requireAuth'
import { GlobalMapProperties, mergeCountsIntoFacilities } from '@/utils/helpers'

export const GLOBAL_PAGE_LOAD_ANALYTIC_NAME = 'Page Load: Global Map View'

export const Global = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const { width: containerWidth, height: containerHeight } =
    useContainerSize(mapContainerRef)

  const router = useRouter()
  const isIPv6RedirectionEnabled = useFeatureFlag(FeatureFlag.ipv6Redirection)
  const isAppHeaderRebrand = useFeatureFlag(FeatureFlag.appHeaderRebrand)

  const { data: orgData } = useOrganizationQuery({
    fetchPolicy: 'cache-and-network',
  })
  const { data: globalFacilitiesData, loading: areFacilitiesLoading } =
    useGlobalFacilitiesQuery({
      fetchPolicy: 'cache-and-network',
    })
  const {
    data: orgFacilitiesIncidentCount,
    loading: areFacilityIncidentsLoading,
  } = useOrganizationFacilitiesIncidentCountQuery({
    fetchPolicy: 'cache-and-network',
  })
  const { data: marketFacilityIncidents } = useIncidentsByMarketFacilityQuery({
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  })

  const allFacilitiesWithoutIncidents: GlobalFacility[] =
    globalFacilitiesData?.globalFacilities?.edges?.map((el) => el.node)

  const mapOfFacilitiesWithIncidents = Object.fromEntries(
    orgFacilitiesIncidentCount?.activeIncidentCountsForAffectedFacilities?.edges.map(
      (e) => [e.node.id, e.node.incidents.totalCount]
    ) ?? []
  )

  const thingsToShowOnMap: GlobalMapProperties[] = useMemo(() => {
    return mergeCountsIntoFacilities(
      allFacilitiesWithoutIncidents,
      mapOfFacilitiesWithIncidents
    )
  }, [allFacilitiesWithoutIncidents, mapOfFacilitiesWithIncidents])

  const lastData: GlobalMapProperties[] = useLastData(thingsToShowOnMap)

  useEffect(() => {
    if (isIPv6RedirectionEnabled) {
      router.replace('/incidents')
    }
  }, [isIPv6RedirectionEnabled, router])

  useEffect(() => {
    startTimer(GLOBAL_PAGE_LOAD_ANALYTIC_NAME)
  }, [])

  useEffect(() => {
    if (!areFacilitiesLoading && !areFacilityIncidentsLoading) {
      trackOrganisationInfo(
        globalFacilitiesData?.globalFacilities.totalCount,
        orgFacilitiesIncidentCount
      )
    }
  }, [areFacilitiesLoading, areFacilityIncidentsLoading])

  if (isIPv6RedirectionEnabled) {
    return <PageLoader />
  }

  return (
    <>
      <Head metaSubTitle='Global' />
      <PreloadCannedVideos />
      <PageLayout>
        <Box>
          {isAppHeaderRebrand ? (
            <DarkAppHeader
              dataTestId='globalPage_header'
              title={orgData?.currentOrganization?.name ?? ''}
              titleLoading={!orgData}
            />
          ) : (
            <Header
              dataTestId='globalPage_header'
              left={
                <Flex alignItems='center' paddingLeft='6'>
                  <Box paddingRight='3'>
                    <HiOutlineOfficeBuilding color='#000' size={24} />
                  </Box>
                  {orgData ? (
                    <Box fontSize='20px' fontWeight='extrabold'>
                      {orgData?.currentOrganization?.name}
                    </Box>
                  ) : (
                    <Skeleton h='24px' w='90px' />
                  )}
                </Flex>
              }
              right={<ActionButtons />}
            />
          )}
          <Box
            h='calc(100vh - 65px)'
            pos='relative'
            ref={mapContainerRef}
            w='full'
          >
            <EditModeFrame />
            <PageSpinnerToast
              loading={areFacilitiesLoading || areFacilityIncidentsLoading}
            />
            <GlobalMap
              facilitiesData={lastData}
              height={containerHeight}
              marketFacilityIncidentsData={marketFacilityIncidents}
              width={containerWidth}
            />
          </Box>
        </Box>
      </PageLayout>
    </>
  )
}

// Default export is needed for NextJS /pages/*
// eslint-disable-next-line import/no-default-export
export default requireAuth(Global)
